import { useContext, useEffect, useState, ChangeEvent } from 'react';
import { Grid, GridItem, TextField, PhoneField, Button, LoadingDots } from '@flos/react-ui';
import { reset, setFieldValidation, setSelectionValue } from '../store/Actions';
import { useStateValue } from '../providers/StateProvider';
import { useConfigContext } from '../context/ConfigContext';
import { UserContext } from '../context/UserContext';
import { FlowContext } from '../context/FlowContext';
import { useLoggerContext } from '../context/LoggerContext';
import { RenderHtml } from './shared/RenderHtml';
import { sendRmoEMail, setError } from '../helpers/shared';
import track, { errorTypes } from 'c9-tracking';
import { BLOCK_TYPES, ProductNames } from '../interfaces/interfaces';

type Props = {
    eventKey?: BLOCK_TYPES;
};

const RMO = ({ eventKey }: Props): JSX.Element => {
    const [{ selection, model, error }, dispatch] = useStateValue();
    const { stakeholder, loggedIn } = useContext(UserContext);
    const config = useConfigContext();
    const flow = useContext(FlowContext);
    const { logRMOError } = useLoggerContext();
    const { firstName, lastName, mobilePhone } = stakeholder ?? {};
    const { product } = useConfigContext();
    const [showReceipt, setShowReceipt] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);

    const hasStakeholderData = loggedIn && !!firstName && !!lastName && !!mobilePhone;
    const productText = product === ProductNames.CHILD ? 'børneforsikring' : 'bilforsikring';

    const commonRmoContent = {
        CUSTOMER_BELOW_18: `<h4>Vi kan desværre ikke give dig en pris online</h4><p>Vi vil dog rigtig gerne hjælpe dig med forsikringen, vi er bare nødt til at tale sammen først.</p><p>Bestil et opkald, så kontakter vi dig hurtigst muligt.</p><p><b>Hvilket nummer må vi ringe til dig på?</b></p>`,
        GENERAL_ERROR: `<h4>Hov, noget gik galt</h4><p>Det ser desværre ud som om, at vi har udfordringer med vores systemer lige nu.<br><br>Vi vil meget gerne hjælpe dig med en pris på din ${productText}.<br><br>Indtast dit mobilnummer, så bliver du ringet op inden for kort tid.<br><br></p>`,
        WILL_NOT_INFORM_CPR: `<h4>Vi ringer dig op</h4><p>Bestil et opkald, så kontakter vi dig hurtigst muligt.</p><p><b>Hvilket nummer må vi ringe til dig på?</b></p>`,
        UNSUPPORTED_ADDRESS_BBR: `<h4>Din adresse skal være mere specifik</h4><p>Din adresse er ikke helt specifik nok. Kontrollér, at du har valgt den rigtige adresse - især etage og dør.</p><p>Hvis du mener, der er tale om en fejl, skal du kontakte os.</p>`
    };

    const rmoContent: Record<ProductNames, Record<string, string>> = {
        CAR: {
            ...commonRmoContent,
            TOO_MANY_CLAIMS: `<p>Så kan vi desværre ikke tilbyde dig at købe forsikringen online.<br><br>Når du har haft flere end 3 skader, er vi nødt til at tale med dig, da prisen afhænger af, hvilke skader du har haft.<br><br>Hvilket mobilnummer må vi ringe til dig på?<br></p>`,
            CAR_TOO_OLD: `<h4>Vi kan ikke give dig en pris online</h4><p>Vi vil dog gerne stadig hjælpe dig, så du kan få en pris på forsikring til din bil, vi er bare nødt til at tale med dig.<br><br>Indtast dit mobilnummer, så bliver du ringet op inden for kort tid.</p>`,
            DFIM: `<h4>Vi kan ikke give dig en pris online</h4><p>Vi kan desværre ikke give dig en pris online.<br><br>Vi vil dog gerne stadig hjælpe dig, så du kan få en pris på forsikring til din bil, vi er bare nødt til at tale med dig.<br><br>Indtast dit mobilnummer, så bliver du ringet op inden for kort tid.<br></p>`,
            CAR_DATA_DEFICIENT: `<h4>Vi kan ikke give dig en pris online</h4><p>Vi kan desværre ikke give dig en pris online.<br><br>Vi vil dog gerne stadig hjælpe dig, så du kan få en pris på forsikring til din bil, vi er bare nødt til at tale med dig.<br><br>Indtast dit mobilnummer, så bliver du ringet op inden for kort tid.<br></p>`,
            CAR_NOT_IN_LIST: `<h4>Vi kan ikke give en pris online</h4><p>Vi kan desværre ikke give dig en pris, når vi ikke kender den præcise bil.</p><p>Vi vil rigtig gerne hjælpe dig med en bilforsikring, vi er bare nødt til at tale sammen først.</p><p>Hvilket nummer må vi ringe til dig på?</p>`,
            EXCLUDED_MANUFACTURER: `<h4>Vi kan ikke give en pris online</h4><p>Vi har særlige krav til sikring af din bil og derfor kan vi ikke give dig en pris online. Vi vil rigtig gerne hjælpe dig med en bilforsikring, vi er bare nødt til at tale sammen først. </p><p>Hvilket nummer må vi ringe til dig på?</p>`,
            LEASING_AGE_INCOMPATIBLE: commonRmoContent.GENERAL_ERROR,
            CAR_OTHER_NEEDS: `<h4>Jeg vil gerne ringes op</h4><p>Vi vil rigtig gerne hjælpe dig med en bilforsikring. Vi er bare nødt til at tale sammen først.<br><br>Hvilket mobilnummer må vi ringe til dig på?</p>`,
            CAR_ALREADY_COVERED: `<h4>Vi kan ikke give dig en pris online</h4><p>Vi vil rigtig gerne hjælpe dig med en pris på en bilforsikring.</p><p>Indtast dit mobilnummer, så bliver du ringet op inden for kort tid.</p>`,
            CAR_NO_CONTENT_37_38: `<h4>Jeg vil gerne kontaktes og høre om muligheder</h4><p>Den valgte bilmodel stiller krav om, at du har din indboforsikring hos os. Vi vil rigtig gerne hjælpe dig med bilforsikring, vi er bare nødt til at tale sammen først.</p><p>Hvilket nummer må vi ringe til dig på?</p>`
            // CAR_DATA_DEFICIENT: `<b>Vi kan ikke give en pris online</b><br><br>Vi kan desværre ikke give dig en pris, da vi mangler data i vores system.<br><br>Vi vil rigtig gerne hjælpe dig med en bilforsikring, vi er bare nødt til at tale sammen først.<br><br>Hvilket nummer må vi ringe til dig på?<br>`,
        },
        CHILD: {
            ...commonRmoContent
        }
    };

    useEffect(() => {
        //setShowReceipt(false);
    }, [selection]);

    useEffect(() => {
        const sh = model[product].stakeholder;
        if (sh && !stakeholder) {
            // validate fields to true if we already have values from GTI
            setFieldValidation(dispatch, 'fullName', !!sh.firstName);
            setFieldValidation(dispatch, 'emailAddress', !!sh.emailAddress);
            setFieldValidation(dispatch, 'phoneNumber', !!sh.mobilePhone);
        }
    }, [stakeholder, dispatch, product, model]);

    useEffect(() => {
        const name: string = hasStakeholderData ? `${firstName} ${lastName}` : selection.fullName ?? '';
        const phoneNumber = selection.phoneNumber ?? mobilePhone ?? '';
        // ensure that phone number has 8 digits and name consists of both first and last name
        // setIsFormValid(phoneNumber.length === 8 && name.split(' ').length > 1);
        setIsFormValid(phoneNumber.length === 8 && !!name);
    }, [firstName, lastName, hasStakeholderData, mobilePhone, selection.fullName, selection.phoneNumber]);

    useEffect(() => {
        if (isSubmitting && !showReceipt) {
            track.form('form-rmo', [
                { id: 'name', value: hasStakeholderData ? `${firstName} ${lastName}` : selection.fullName ?? '' },
                { id: 'phone', value: selection.phoneNumber ?? mobilePhone ?? '' }
            ]);
            try {
                (async () => {
                    await sendRmoEMail({ config, selection, model, error, flow, eventKey, logRMOError });
                    setShowReceipt(true);
                    track.info('The customer has submitted an RMO');
                })();
            } catch (err) {
                const errorData = {
                    errorMessage: 'Der skete en fejl. Prøv igen senere.',
                    hasError: true,
                    operation: 'Sending the RMO failed'
                };
                setError({ dispatch, ...errorData });
                setShowReceipt(false);
                track.fault('An error occured while handling RMO', errorTypes.tech, 'RMO: failed');
            }
        }
    }, [
        config,
        dispatch,
        eventKey,
        firstName,
        flow,
        hasStakeholderData,
        isSubmitting,
        showReceipt,
        lastName,
        logRMOError,
        mobilePhone,
        model,
        selection,
        error
    ]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSelectionValue(dispatch, selection, { key: e.target.name, value: e.target.value }, true);
    };

    const handleSubmit = async () => {
        if (!isSubmitting) {
            setIsSubmitting(true);
        }
    };

    const handleReceiptButtonClick = () => {
        setShowReceipt(false);
        setIsSubmitting(false);
        if (flow.blockType === BLOCK_TYPES.CAR_OTHER_NEEDS) {
            setSelectionValue(dispatch, selection, { key: 'isNeedOtherCoverage', value: false }, true);
        } else {
            reset(dispatch, false);
            const originalHost = window.topContext.config.getSiteConfig().originalHost;
            window.location.replace(`${window.location.protocol}//${originalHost}${config.appBasePath}`);
        }
    };

    return showReceipt ? (
        <>
            <h4>Tak. Vi ringer til dig hurtigst muligt </h4>
            <p>Bemærk, at der dog kan gå op til to arbejdsdage.</p>
            <Button onClick={handleReceiptButtonClick}>Okay</Button>
        </>
    ) : (
        <>
            <RenderHtml>
                {rmoContent[config.product]?.[eventKey ?? flow.blockType ?? commonRmoContent.GENERAL_ERROR]}
            </RenderHtml>
            <Grid gap={'none'}>
                <GridItem span={12}>
                    <Grid gap={5}>
                        <GridItem span={12}>
                            <TextField
                                label="Navn"
                                value={hasStakeholderData ? `${firstName} ${lastName}` : selection.fullName ?? ''}
                                name="fullName"
                                readOnly={hasStakeholderData}
                                disabled={hasStakeholderData ?? isSubmitting}
                                onChange={(e) => handleChange(e)}
                            ></TextField>
                        </GridItem>
                    </Grid>
                </GridItem>
                <GridItem span={12}>
                    <PhoneField
                        label="Mobilnummer"
                        value={selection.phoneNumber ?? mobilePhone ?? ''}
                        name="phoneNumber"
                        onChange={(e) => handleChange(e)}
                        disabled={isSubmitting}
                    ></PhoneField>
                </GridItem>
                <GridItem span={12}>
                    <Button style={{ marginTop: 20, marginBottom: 20 }} disabled={!isFormValid} onClick={handleSubmit}>
                        Ring mig op
                    </Button>
                    {isSubmitting && <LoadingDots centered={true} />}
                </GridItem>
            </Grid>
        </>
    );
};

export { RMO };
